import React from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, MapPin } from 'lucide-react';

const LABEL_TEXT_CLASS = 'block mb-1 text-xs font-medium text-gray-600';
// No text colour here: two colour utilities on one element resolve by
// stylesheet order, not class order, so each control sets exactly one.
const CONTROL_BASE_CLASS = 'block w-full h-10 px-3 text-sm bg-white border rounded-md '
  + 'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-colors';
const controlClass = (hasError) => `${CONTROL_BASE_CLASS} ${hasError
  ? 'border-red-400 focus:border-red-500'
  : 'border-gray-300 focus:border-[#045692]'}`;

// Addresses are captured in block letters - it is what the tax invoice
// prints. The free-text fields; the state is a code and the PIN is digits.
const BLOCK_LETTER_FIELDS = ['houseNo', 'area', 'city', 'landmark'];
const toBlockLetters = (value) => String(value ?? '').toUpperCase();

// For seeding the form from a stored address (the order's copy, a profile's
// mixed-case one), so a prefilled value is block letters before it is touched.
export function toBlockLetterAddress(address) {
  if (!address) return address;
  const upper = { ...address };
  BLOCK_LETTER_FIELDS.forEach((field) => {
    if (upper[field] != null) upper[field] = toBlockLetters(upper[field]);
  });
  return upper;
}

// Whether two addresses say the same thing, as the server's isSameAddress
// judges it: case and spacing ignored, the state by code when both carry one.
const comparable = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
export function isSameAddress(a, b) {
  if (!a || !b) return false;
  const fields = ['houseNo', 'area', 'landmark', 'city', 'pinCode'];
  if (!fields.every((field) => comparable(a[field]) === comparable(b[field]))) return false;
  if (a.stateCode && b.stateCode) return comparable(a.stateCode) === comparable(b.stateCode);
  return comparable(a.state) === comparable(b.state);
}

const registrationShape = PropTypes.shape({
  name: PropTypes.string,
  ref: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onBlur: PropTypes.func,
});

function FieldLabel({ text, required }) {
  return (
    <span className={LABEL_TEXT_CLASS}>
      {text}
      {required
        ? <span className="text-red-500 ml-0.5">*</span>
        : <span className="text-gray-400 font-normal ml-1">(optional)</span>}
    </span>
  );
}
FieldLabel.propTypes = {
  text: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
};

function FieldError({ message }) {
  return <p className="min-h-4 mt-0.5 text-[11px] leading-4 text-red-500">{message}</p>;
}
FieldError.propTypes = {
  message: PropTypes.string,
};
FieldError.defaultProps = {
  message: undefined,
};

// The control sits inside its label and the react-hook-form registration is
// bound field by field rather than spread - the same pattern as
// TermsAcceptance. The registration's own onChange is deliberately not bound:
// each change goes through onValueChange, which keeps userDetails and the form
// store in step (and validates).
function AddressTextField({
  id, label, required, placeholder, inputMode, autoCapitalize, maxLength, transform, registration,
  value, onValueChange, error, className,
}) {
  // The transformed value is written back into the DOM before state updates,
  // with the caret restored: otherwise React sees its value differ from what
  // was typed and snaps the caret to the end on every mid-word keystroke.
  const handleChange = (e) => {
    const input = e.target;
    const next = transform ? transform(input.value) : input.value;
    if (next !== input.value) {
      const { selectionStart, selectionEnd } = input;
      const shift = next.length - input.value.length;
      input.value = next;
      if (selectionStart != null) {
        input.setSelectionRange(
          Math.max(0, selectionStart + shift),
          Math.max(0, selectionEnd + shift),
        );
      }
    }
    onValueChange(next);
  };

  return (
    <div className={className}>
      <label htmlFor={id} className="block">
        <FieldLabel text={label} required={required} />
        <input
          type="text"
          id={id}
          name={registration.name}
          ref={registration.ref}
          onBlur={registration.onBlur}
          value={value ?? ''}
          placeholder={placeholder}
          inputMode={inputMode}
          autoCapitalize={autoCapitalize}
          maxLength={maxLength}
          autoComplete="off"
          aria-invalid={Boolean(error)}
          onChange={handleChange}
          className={`${controlClass(Boolean(error))} text-gray-900`}
        />
      </label>
      <FieldError message={error} />
    </div>
  );
}
AddressTextField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  inputMode: PropTypes.string,
  autoCapitalize: PropTypes.string,
  maxLength: PropTypes.number,
  transform: PropTypes.func,
  registration: registrationShape.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onValueChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  className: PropTypes.string,
};
AddressTextField.defaultProps = {
  required: true,
  placeholder: undefined,
  inputMode: undefined,
  autoCapitalize: undefined,
  maxLength: undefined,
  transform: undefined,
  value: '',
  error: undefined,
  className: undefined,
};

// One address card, reused for both the GST billing address and the delivery
// (shipping) address. `fieldPrefix` namespaces the react-hook-form fields
// ('billing.' registers billing.houseNo etc., so both cards can live in one
// form) and `addressKey` names the slice of userDetails this card edits.
// `children` renders as the card's footer (the billing card carries the
// "same as billing" toggle there). The defaults keep the original
// delivery-address usage source-compatible.
function AddressDetails({
  setValue, register, errors, userDetails, setUserDetails, states,
  title = 'Delivery Address',
  subtitle = 'This address will be used for all communication pertaining to this order',
  fieldPrefix = '',
  addressKey = 'address',
  icon: Icon = MapPin,
  children = null,
}) {
  const address = userDetails?.[addressKey];
  // With a prefix like 'billing.', react-hook-form nests the errors under
  // that key; without one they stay at the top level, as before.
  const fieldErrors = fieldPrefix ? errors?.[fieldPrefix.replace(/\.$/, '')] : errors;
  // DOM ids must stay unique when the billing and shipping cards render together.
  const domId = (id) => `${fieldPrefix}${id}`;
  const fieldName = (field) => `${fieldPrefix}${field}`;
  const updateField = (field, value) => {
    setUserDetails({ ...userDetails, [addressKey]: { ...address, [field]: value } });
    setValue(fieldName(field), value, { shouldValidate: true });
  };
  const textField = ({
    field, id, label, required = true, placeholder, inputMode, maxLength, sanitize, className,
  }) => {
    const isBlockLetters = BLOCK_LETTER_FIELDS.includes(field);
    return (
      <AddressTextField
        id={domId(id)}
        label={label}
        required={required}
        placeholder={placeholder}
        inputMode={inputMode}
        // A hint to phone keyboards; the transform is what actually enforces it.
        autoCapitalize={isBlockLetters ? 'characters' : undefined}
        maxLength={maxLength}
        transform={sanitize || (isBlockLetters ? toBlockLetters : undefined)}
        className={className}
        registration={register(fieldName(field))}
        value={address?.[field]}
        onValueChange={(value) => updateField(field, value)}
        error={fieldErrors?.[field]?.message}
      />
    );
  };
  const stateRegistration = register(fieldName('state'));
  const stateError = fieldErrors?.state?.message;

  return (
    <section className="mb-6 bg-white border border-[#B8E5FF] rounded-xl shadow-sm overflow-hidden">
      <header className="flex items-center gap-3 px-4 pt-4 pb-3 md:px-5 border-b border-gray-100">
        <div className="p-2 bg-blue-50 rounded-lg shrink-0">
          <Icon className="w-5 h-5 text-blue-600" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 leading-tight">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs md:text-sm text-gray-500">{subtitle}</p>}
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-x-3 gap-y-1 px-4 pt-4 pb-2 md:px-5">
        {textField({
          field: 'houseNo', id: 'house_no', label: 'House / Flat / Floor', placeholder: 'e.g. 12B, 3rd Floor', className: 'md:col-span-2',
        })}
        {textField({
          field: 'area', id: 'address', label: 'Apartment / Road / Area', placeholder: 'Building, street, locality', className: 'md:col-span-4',
        })}
        {textField({
          field: 'city', id: 'city', label: 'City', placeholder: 'City', className: 'md:col-span-2',
        })}
        <div className="md:col-span-2">
          <label htmlFor={domId('state')} className="block">
            <FieldLabel text="State" required />
            {/* appearance-none + our own chevron: the native select's own padding
                indents its value past the text inputs' on some platforms. */}
            <span className="relative block">
              <select
                id={domId('state')}
                name={stateRegistration.name}
                ref={stateRegistration.ref}
                onBlur={stateRegistration.onBlur}
                value={address?.state ?? ''}
                aria-invalid={Boolean(stateError)}
                onChange={(e) => updateField('state', e.target.value)}
                className={`${controlClass(Boolean(stateError))} appearance-none pr-9 ${address?.state ? 'text-gray-900' : 'text-gray-400'}`}
              >
                <option value="">Select state</option>
                {states.map((st) => (
                  <option key={st.stateCode} value={st.stateCode} className="text-gray-900">{st.name}</option>
                ))}
              </select>
              <ChevronDown
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </label>
          <FieldError message={stateError} />
        </div>
        {textField({
          field: 'pinCode',
          id: 'pin_code',
          label: 'PIN Code',
          placeholder: '6 digits',
          inputMode: 'numeric',
          maxLength: 6,
          // Digits only, capped at six - the schema wants exactly /^\d{6}$/.
          sanitize: (value) => value.replace(/\D/g, '').slice(0, 6),
          className: 'md:col-span-2',
        })}
        {textField({
          field: 'landmark', id: 'landmark', label: 'Landmark', required: false, placeholder: 'Nearby landmark', className: 'md:col-span-6',
        })}
      </div>

      {children && (
        <footer className="px-4 py-3 md:px-5 bg-gray-50 border-t border-gray-100">
          {children}
        </footer>
      )}
    </section>
  );
}
AddressDetails.propTypes = {
  userDetails: PropTypes.object,
  setUserDetails: PropTypes.func,
  states: PropTypes.array,
  errors: PropTypes.object,
  setValue: PropTypes.func,
  register: PropTypes.func,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  fieldPrefix: PropTypes.string,
  addressKey: PropTypes.string,
  icon: PropTypes.elementType,
  children: PropTypes.node,
};
export default AddressDetails;
